import { useCallback, useRef } from 'react';
import Slider from 'react-slick';
// Types
import {
  GetAthleteStreams_liveAndUpcomingStreams_entities,
  GetAthleteStreams_pastStreams_entities,
} from 'api/streams/types/GetAthleteStreams';
// Hooks
import { useBreakpoints } from 'hooks';
// Components
import Section from 'components/common3/Section/Section';
import RelatedStreams from 'components/Athlete/Streams/RelatedStreams/RelatedStreams';
// UI3
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './OtherStreamsSection.module.scss';

type OtherStreamsSectionProps = {
  streams:
    | GetAthleteStreams_liveAndUpcomingStreams_entities[]
    | GetAthleteStreams_pastStreams_entities[];
  seeAllLink: string;
  seeAllLinkTitle: string;
  className?: string;
  ppvStreamId?: string;
};

// Match RelatedProductsList: desktop arrows >= 4 items, mobile arrows >= 3 items
const MIN_STREAMS_FOR_DESKTOP_ARROWS = 4;
const MIN_STREAMS_FOR_MOBILE_ARROWS = 3;

const OtherStreamsSection = ({
  streams,
  seeAllLink,
  seeAllLinkTitle,
  className,
  ppvStreamId,
}: OtherStreamsSectionProps): JSX.Element | null => {
  const carouselRef = useRef<InstanceType<typeof Slider> | null>(null);
  const { isBreakpointL } = useBreakpoints();
  const streamCount = streams.length;
  const showDesktopArrows =
    isBreakpointL && streamCount >= MIN_STREAMS_FOR_DESKTOP_ARROWS;
  const showMobileArrows =
    !isBreakpointL && streamCount >= MIN_STREAMS_FOR_MOBILE_ARROWS;

  const handlePrev = useCallback(() => {
    carouselRef.current?.slickPrev();
  }, []);

  const handleNext = useCallback(() => {
    carouselRef.current?.slickNext();
  }, []);

  if (streams.length === 0) {
    return null;
  }

  return (
    <Section
      title="Other Streams"
      seeAllLink={seeAllLink}
      seeAllLinkTitle={seeAllLinkTitle}
      className={className}
      headerPadding="none"
      headerRightContent={
        showDesktopArrows ? (
          <div className={styles.desktopArrows}>
            <button
              type="button"
              onClick={handlePrev}
              className={styles.arrowButton}
              aria-label="Previous"
            >
              <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className={styles.arrowButton}
              aria-label="Next"
            >
              <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
            </button>
          </div>
        ) : null
      }
    >
      <div className={styles.carouselWithArrows}>
        <RelatedStreams
          ref={carouselRef}
          streams={streams}
          ppvStreamId={ppvStreamId}
          padding="none"
        />
        {showMobileArrows ? (
          <div className={styles.mobileArrows}>
            <button
              type="button"
              onClick={handlePrev}
              className={styles.arrowButton}
              aria-label="Previous"
            >
              <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className={styles.arrowButton}
              aria-label="Next"
            >
              <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
            </button>
          </div>
        ) : null}
      </div>
    </Section>
  );
};

export default OtherStreamsSection;
